import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';
import { t, useUiLocale } from '@/i18n/locale';
import { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { CompanionContext } from './CompanionContext';
import { loadBuddyDraft } from './companionDraft';
import './companion.css';

/** First-login entry: only a server-saved profile completes buddy setup. */
export function CompanionSetupPrompt() {
  useUiLocale();
  const session = useContext(CompanionContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dialog = useRef<HTMLDialogElement>(null);
  const startButton = useRef<HTMLButtonElement>(null);
  const open =
    !!session &&
    session.audience === 'kid' &&
    !!session.snapshot &&
    !session.setupDeferred &&
    !session.loading &&
    !session.loadError &&
    !session.snapshot.profile &&
    !pathname.startsWith('/learn/companion');
  useEffect(() => {
    const element = dialog.current;
    if (!open || !element) return;
    element.showModal();
    startButton.current?.focus();
    return () => element.close();
  }, [open]);
  if (!open || !session) return null;
  const resume = !!loadBuddyDraft(session.kidId, null);
  const start = () => {
    // This welcome replaces the generic studio slideshow for new buddy owners.
    // It never marks buddy setup complete: the API profile remains authoritative.
    try {
      localStorage.setItem('airbotix.learn.welcomed', '1');
    } catch {
      /* optional */
    }
    session.deferSetup();
    navigate('/learn/companion/design');
  };
  return (
    <dialog
      ref={dialog}
      className="companion-dialog text-center"
      aria-labelledby="buddy-setup-title"
      aria-describedby="buddy-setup-description"
      onCancel={() => session.deferSetup()}
    >
      <div className="flex justify-end">
        <LanguageSwitcher />
      </div>
      <div className="relative mx-auto mt-3 rounded-3xl bg-wash-sky p-4">
        <img
          src="/media/companion/workshop/assets/tuantuan-reference.png"
          alt=""
          className="mx-auto h-44 w-44 object-contain sm:h-52 sm:w-52"
        />
        <span className="inline-flex items-center gap-1 rounded-full bg-canvas-pure px-3 py-1 text-xs font-bold text-ink">
          <Sparkles size={14} aria-hidden="true" />
          {t('Made by you')}
        </span>
      </div>
      <h1
        id="buddy-setup-title"
        className="mt-5 text-2xl font-extrabold leading-tight text-ink sm:text-3xl"
      >
        {resume ? t('Let’s finish your AI buddy') : t('Let’s design your AI buddy')}
      </h1>
      <p id="buddy-setup-description" className="mt-3 text-sm leading-relaxed text-ink-soft">
        {resume
          ? t('Your choices are saved. Let’s pick up where you left off.')
          : t(
              'Pick a friend, choose a look, and give them a name. Make a buddy that feels like you.',
            )}
      </p>
      <button
        ref={startButton}
        type="button"
        className="btn-pill-primary mt-6 w-full"
        onClick={start}
      >
        {t('Let’s go →')}
      </button>
      <button type="button" className="btn-pill-ghost mt-2 w-full" onClick={session.deferSetup}>
        {t('I’ll do this later')}
      </button>
    </dialog>
  );
}
